// Imposter mask pure helpers. No React, no Three.js.
//
// ── Codex (Imposter) ──
//   "(3) → Banish an Avatar from your collection to mask yourself. Gain
//    their abilities until damaged or until you put on a new mask."
//
// Masking is implemented by mutating the imposter card instance's
// identity fields to match the picked avatar. Every downstream pipeline
// `resolveAvatarRules` (cardId lookup), the sidecar automation lookup
// (`sorceryCards.find(c => c.unique_id === cardInstance.cardId)`),
// `recomputeStaticAuras`, the action-menu. Already keys off `cardId`,
// `name`, `imageUrl`, `subTypes`, `elements`, and the hydrated
// `_printedKeywords` / `_movementBonus` / `_rangedValue` fields. Swap
// those and the masked avatar's abilities surface for free.
//
// The original identity is snapshot under `_originalIdentity` so an
// "until damaged" or "fresh mask" revert can restore it. `_isImposter`
// is stamped at spawn (see `GameBoard.addCardToTable`) so the Mask
// ability stays in the action menu even while masked. Without it, the
// masked avatar's cardId would resolve to a different rule object and
// the Mask entry would vanish, making the swap one-way.

/**
 * Find a Sorcery catalog card by its `unique_id`. Returns null if the
 * caller passes a falsy id or the catalog is empty.
 */
export function findCatalogCard(sorceryCards, cardId) {
  if (!Array.isArray(sorceryCards) || !cardId) return null;
  return sorceryCards.find((c) => c.unique_id === cardId) || null;
}

/**
 * Build the "snapshot" object. The fields the imposter has to remember
 * before they get overwritten. Restoring this back onto the instance
 * fully reverts the masquerade.
 */
export function snapshotIdentity(cardInstance) {
  if (!cardInstance) return null;
  return {
    cardId: cardInstance.cardId,
    name: cardInstance.name,
    imageUrl: cardInstance.imageUrl,
    imagePath: cardInstance.imagePath,
    foiling: cardInstance.foiling,
    subTypes: Array.isArray(cardInstance.subTypes) ? [...cardInstance.subTypes] : [],
    elements: Array.isArray(cardInstance.elements) ? [...cardInstance.elements] : [],
    _printedKeywords: Array.isArray(cardInstance._printedKeywords)
      ? [...cardInstance._printedKeywords]
      : [],
    _movementBonus: cardInstance._movementBonus || 0,
    _rangedValue: cardInstance._rangedValue || 0,
  };
}

/**
 * Mutate the imposter instance to wear the picked avatar's face. Pulls
 * fresh keyword / movement / ranged data from the catalog so layer
 * computations and combat math see the masked avatar's printed values.
 *
 * "Gain their abilities" is ADDITIVE. The wearer keeps its own
 * printed keywords (union) and the better of the two Movement+N /
 * Ranged values. Only the identity fields (cardId, name, art,
 * typeline) are replacements.
 *
 * Returns the snapshot of the previous identity. The caller is
 * responsible for stashing it on the instance (typically
 * `cardInstance._originalIdentity`). Keeping the side effect in one
 * place makes the revert symmetric.
 */
export function applyMaskIdentity(cardInstance, sourceCard) {
  if (!cardInstance || !sourceCard) return null;
  const snapshot = snapshotIdentity(cardInstance);
  const printing = sourceCard.printings?.[0];
  const imageUrl = printing?.image_url || '';
  cardInstance.cardId = sourceCard.unique_id;
  cardInstance.name = sourceCard.name;
  cardInstance.imageUrl = imageUrl;
  cardInstance.imagePath = imageUrl.replace(/^https?:\/\/[^/]+/, '');
  cardInstance.foiling = printing?.foiling || cardInstance.foiling || 'S';
  cardInstance.subTypes = Array.isArray(sourceCard.traits) ? [...sourceCard.traits] : [];
  cardInstance.elements = Array.isArray(sourceCard.elements)
    ? sourceCard.elements.map((e) => String(e?.name || e?.id || e).toLowerCase())
    : [];
  const gainedKeywords = Array.isArray(sourceCard.printedKeywords)
    ? sourceCard.printedKeywords
    : [];
  cardInstance._printedKeywords = [...new Set([...snapshot._printedKeywords, ...gainedKeywords])];
  cardInstance._movementBonus = Math.max(snapshot._movementBonus, sourceCard.movementBonus || 0);
  cardInstance._rangedValue = Math.max(snapshot._rangedValue, sourceCard.rangedValue || 0);
  return snapshot;
}

/**
 * Restore the snapshot back onto the instance. Mirror of applyMaskIdentity.
 */
export function restoreIdentity(cardInstance, snapshot) {
  if (!cardInstance || !snapshot) return;
  cardInstance.cardId = snapshot.cardId;
  cardInstance.name = snapshot.name;
  cardInstance.imageUrl = snapshot.imageUrl;
  cardInstance.imagePath = snapshot.imagePath;
  cardInstance.foiling = snapshot.foiling;
  cardInstance.subTypes = Array.isArray(snapshot.subTypes) ? [...snapshot.subTypes] : [];
  cardInstance.elements = Array.isArray(snapshot.elements) ? [...snapshot.elements] : [];
  cardInstance._printedKeywords = Array.isArray(snapshot._printedKeywords)
    ? [...snapshot._printedKeywords]
    : [];
  cardInstance._movementBonus = snapshot._movementBonus || 0;
  cardInstance._rangedValue = snapshot._rangedValue || 0;
}

/**
 * Predicate: is this catalog card a legal mask target? Per the codex
 * the imposter banishes "an Avatar from your collection", so any
 * Avatar-type card in the collection pile qualifies.
 */
export function isLegalMaskTarget(card) {
  return !!card && (card.type === 'Avatar' || card._sorceryCategory === 'Avatar');
}

/**
 * The imposter's TRUE cardId while it wears a mask, or null when it is
 * bare-faced. The codex says the imposter "gains their abilities." It
 * does not become them, so it keeps its own native abilities (the
 * tap-to-play/draw-site builtin, the Mask itself). Callers resolve the
 * imposter's own avatar rules off this id even though `cardId` has been
 * swapped to the borrowed face, so a Pathfinder-style mask that
 * replaces the builtin tap can't strip the imposter's own site tap.
 */
export function maskedImposterBaseCardId(cardInstance) {
  if (!cardInstance?._isImposter) return null;
  const originalId = cardInstance._originalIdentity?.cardId;
  return originalId && originalId !== cardInstance.cardId ? originalId : null;
}
